import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { ArrowRight, Building2, Users } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [userType, setUserType] = useState("client");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const user = await login(email, password, userType);
            const from = location.state?.from;
            if (from) return navigate(from, { replace: true });
            if (user.user_type === "admin") navigate("/dashboard/admin");
            else if (user.user_type === "provider") navigate("/dashboard/provider");
            else navigate("/dashboard/client");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pt-16 min-h-screen grid place-items-center bg-zinc-50">
            <div className="max-w-md w-full mx-4 bg-white border border-zinc-200 p-10">
                <div className="text-[10px] uppercase tracking-label text-zinc-500">Flexible Workspace Group</div>
                <h1 className="font-display text-4xl font-semibold tracking-tight mt-3">Sign in</h1>

                <div className="mt-8 grid grid-cols-3 gap-px bg-zinc-200">
                    <button
                        type="button"
                        data-testid="login-type-client"
                        onClick={() => setUserType("client")}
                        className={`p-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-label ${userType === "client" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                    >
                        <Users className="h-4 w-4" strokeWidth={1.5} /> Seeker
                    </button>
                    <button
                        type="button"
                        data-testid="login-type-provider"
                        onClick={() => setUserType("provider")}
                        className={`p-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-label ${userType === "provider" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                    >
                        <Building2 className="h-4 w-4" strokeWidth={1.5} /> Provider
                    </button>
                    <button
                        type="button"
                        data-testid="login-type-admin"
                        onClick={() => setUserType("admin")}
                        className={`p-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-label ${userType === "admin" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                    >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"/></svg>
                        FWG
                    </button>
                </div>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-label text-zinc-500">Email</label>
                        <input
                            data-testid="login-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-label text-zinc-500">Password</label>
                        <input
                            data-testid="login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full border border-zinc-300 px-3 py-2.5 mt-1 focus:border-zinc-950 focus:outline-none"
                        />
                    </div>
                    {error && (
                        <div data-testid="login-error" className="bg-red-50 border-l-2 border-red-400 p-3 text-xs text-red-900">{error}</div>
                    )}
                    <button
                        data-testid="login-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="w-full group bg-zinc-950 text-white py-4 flex items-center justify-between px-5 hover:bg-[#0044FF] transition-colors disabled:opacity-40"
                    >
                        <span className="text-sm font-medium">{loading ? "Signing in…" : "Sign in"}</span>
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                    </button>
                </form>

                <div className="mt-6 text-xs text-zinc-600">
                    New to FWG?{" "}
                    <Link to="/register" data-testid="go-to-register" className="underline underline-offset-4 text-zinc-950">Create an account</Link>
                </div>
            </div>
        </div>
    );
}
